import { useCallback, useState } from 'react';

import ConfirmationDialog from '@sayyesschool/frontend/components/confirmation-dialog';
import FormDialog from '@sayyesschool/frontend/components/form-dialog';
import LessonPillGroup from '@sayyesschool/frontend/components/lesson-pill-group';
import PageSection from '@sayyesschool/frontend/components/page-section';
import { getNearestDay } from '@sayyesschool/frontend/features/enrollments';
import { useLessons } from '@sayyesschool/frontend/features/lessons';
import { useBoolean } from '@sayyesschool/frontend/hooks/state';
import { IconButton, Menu } from '@sayyesschool/frontend/ui-components';

import LessonForm from '@/components/lessons/lesson-form';
import LessonsForm from '@/components/lessons/lessons-form';

export default function EnrollmentLessons({ enrollment }) {
    const [lessons, actions] = useLessons(`enrollmentId=${enrollment.id}`);

    const [lesson, setLesson] = useState();
    const [isNewLessonFormOpen, toggleNewLessonFormOpen] = useBoolean(false);
    const [isEditLessonFormOpen, toggleEditLessonFormOpen] = useBoolean(false);
    const [isLessonsFormOpen, toggleLessonsFormOpen] = useBoolean(false);
    const [isConfirmationDialogOpen, toggleConfirmationDialogOpen] = useBoolean(false);

    const createLessons = useCallback(lessons => {
        lessons.forEach(lesson => {
            lesson.enrollmentId = enrollment.id;
            lesson.learnerId = enrollment.learnerId;
            lesson.teacherId = enrollment.teacherId;
        });

        return actions.createLessons(lessons)
            .then(() => toggleLessonsFormOpen(false));
    }, [enrollment, actions, toggleLessonsFormOpen]);

    const createLesson = useCallback(data => {
        data.enrollmentId = enrollment.id;

        return actions.createLesson(data)
            .then(() => toggleNewLessonFormOpen(false));
    }, [enrollment, actions, toggleNewLessonFormOpen]);

    const updateLesson = useCallback(data => {
        return actions.updateLesson(lesson.id, data)
            .then(() => toggleEditLessonFormOpen(false));
    }, [lesson, actions, toggleEditLessonFormOpen]);

    const deleteLesson = useCallback(() => {
        return actions.deleteLesson(lesson.id)
            .then(() => toggleConfirmationDialogOpen(false));
    }, [lesson, actions, toggleConfirmationDialogOpen]);

    const handleEdit = useCallback(lesson => {
        setLesson(lesson);
        toggleEditLessonFormOpen(true);
    }, [toggleEditLessonFormOpen]);

    const handleDelete = useCallback(lesson => {
        setLesson(lesson);
        toggleConfirmationDialogOpen(true);
    }, [toggleConfirmationDialogOpen]);

    const hasSchedule = enrollment.schedule?.length > 0;

    return (
        <PageSection
            className="EnrollmentLessons"
            title="Занятия"
            actions={
                <Menu
                    trigger={
                        <IconButton
                            icon="add"
                            title="Создать"
                            size="sm"
                            disabled={!hasSchedule}
                        />
                    }
                    items={[
                        {
                            key: 'single',
                            content: 'Создать вне расписания',
                            onClick: toggleNewLessonFormOpen
                        },
                        {
                            key: 'multiple',
                            content: 'Создать по рассписанию',
                            onClick: toggleLessonsFormOpen
                        }
                    ]}
                />
            }
        >
            {lessons?.length > 0 &&
                <LessonPillGroup
                    lessons={lessons}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                />
            }

            <FormDialog
                form="create-lesson-form"
                title="Новое занятие"
                open={isNewLessonFormOpen}
                onClose={toggleNewLessonFormOpen}
            >
                <LessonForm
                    id="create-lesson-form"
                    lesson={{
                        date: getNearestDay(enrollment.schedule),
                        duration: enrollment.lessonDuration,
                        learnerId: enrollment?.learner?.id,
                        teacherId: enrollment?.teacher?.id
                    }}
                    onSubmit={createLesson}
                />
            </FormDialog>

            <FormDialog
                form="lessons-form"
                title="Новые занятия"
                open={isLessonsFormOpen}
                onClose={toggleLessonsFormOpen}
            >
                <LessonsForm
                    id="lessons-form"
                    enrollment={enrollment}
                    onSubmit={createLessons}
                />
            </FormDialog>

            <FormDialog
                form="edit-lesson-form"
                title="Редактирование занятия"
                open={isEditLessonFormOpen}
                onClose={toggleEditLessonFormOpen}
            >
                <LessonForm
                    id="edit-lesson-form"
                    lesson={lesson}
                    onSubmit={updateLesson}
                />
            </FormDialog>

            <ConfirmationDialog
                title="Подтвердите действие"
                message="Вы действительно хотите удалить урок?"
                open={isConfirmationDialogOpen}
                onConfirm={deleteLesson}
                onClose={toggleConfirmationDialogOpen}
            />
        </PageSection>
    );
}